import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, AlertCircle } from 'lucide-react';
import { ClientPicker } from '../components/ClientPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewTransaction() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clientId, setClientId] = useState(searchParams.get('client') || '');
  const [type, setType] = useState(searchParams.get('type') || 'income');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [taxDeductible, setTaxDeductible] = useState(false);
  const [category, setCategory] = useState('');
  const [taxNote, setTaxNote] = useState('');
  const [deductibleAmount, setDeductibleAmount] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Expense category state
  const [expenseCategories, setExpenseCategories] = useState(null);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [selectedCategoryData, setSelectedCategoryData] = useState(null);
  const [mileageRate, setMileageRate] = useState(0.67);
  
  // Mileage tracking
  const [isMileage, setIsMileage] = useState(false);
  const [miles, setMiles] = useState('');

  useEffect(() => {
    if (type === 'expense') {
      fetchExpenseCategories();
    }
  }, [type]);

  const fetchExpenseCategories = async () => {
    try {
      const res = await axios.get(`${API}/expense-categories`, { withCredentials: true });
      setExpenseCategories(res.data);
      setMileageRate(res.data.mileage_rate);
    } catch (err) {
      console.error('Failed to load expense categories:', err);
    }
  };

  const handleCategorySelect = (categoryName, categoryData) => {
    setCategory(categoryName);
    setSelectedCategoryData(categoryData);
    setTaxNote(categoryData?.tax_note || '');
    setTaxDeductible(true); // Auto-check for standard categories
    setShowCategoryPicker(false);
    setCategorySearch('');
    
    // Handle special cases
    if (categoryData?.is_mileage) {
      setIsMileage(true);
      setAmount('');
      setMiles('');
    } else {
      setIsMileage(false);
    }
    
    // Handle partial deduction (meals)
    if (categoryData?.partial_deduction) {
      // Will calculate deductible_amount on amount change
    }
  };

  const handleMilesChange = (value) => {
    setMiles(value);
    if (value) {
      const calculatedAmount = parseFloat(value) * mileageRate;
      setAmount(calculatedAmount.toFixed(2));
    } else {
      setAmount('');
    }
  };

  const handleAmountChange = (value) => {
    setAmount(value);
    
    // If this is a meal category with 50% deduction
    if (selectedCategoryData?.partial_deduction && value) {
      const fullAmount = parseFloat(value);
      const deductible = fullAmount * selectedCategoryData.partial_deduction;
      setDeductibleAmount(deductible);
    } else {
      setDeductibleAmount(null);
    }
  };

  const handleClientChange = (id, _name) => {
    setClientId(id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!clientId) {
      setError('Please select a client');
      setLoading(false);
      return;
    }

    try {
      await axios.post(
        `${API}/transactions`,
        {
          client_id: clientId,
          type,
          description,
          amount: parseFloat(amount),
          date,
          category: category || undefined,
          tax_deductible: type === 'expense' ? taxDeductible : false,
          tax_note: taxNote || undefined,
          deductible_amount: deductibleAmount || undefined,
          invoice_status: type === 'invoice' ? 'unpaid' : undefined,
          due_date: type === 'invoice' ? dueDate : undefined,
        },
        { withCredentials: true }
      );
      navigate(`/clients/${clientId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  const renderCategoryPicker = () => {
    if (!expenseCategories) return null;

    const allCategories = [];
    Object.entries(expenseCategories.standard).forEach(([group, items]) => {
      items.forEach(item => {
        allCategories.push({ ...item, group });
      });
    });

    const filteredCategories = allCategories.filter(cat =>
      cat.name.toLowerCase().includes(categorySearch.toLowerCase()) ||
      cat.group.toLowerCase().includes(categorySearch.toLowerCase())
    );

    return (
      <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded-lg shadow-lg max-h-96 overflow-y-auto">
        <div className="sticky top-0 bg-surface border-b border-border p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Search categories..."
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
          </div>
        </div>
        
        {Object.entries(expenseCategories.standard).map(([group, items]) => {
          const groupItems = items.filter(item =>
            item.name.toLowerCase().includes(categorySearch.toLowerCase()) ||
            group.toLowerCase().includes(categorySearch.toLowerCase())
          );
          
          if (groupItems.length === 0) return null;
          
          return (
            <div key={group} className="border-b border-border last:border-0">
              <div className="px-4 py-2 bg-surface-raised text-xs font-medium text-text-muted uppercase tracking-wide">
                {group}
              </div>
              {groupItems.map((item) => (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => handleCategorySelect(item.name, item)}
                  className="w-full text-left px-4 py-2 hover:bg-surface-hover transition-colors text-foreground text-sm"
                >
                  {item.name}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-foreground mb-4 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <h1 className="text-3xl font-serif font-bold text-foreground mb-6" data-testid="new-transaction-title">
          New {type === 'invoice' ? 'Invoice' : type === 'income' ? 'Income' : 'Expense'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Client *
            </label>
            <ClientPicker value={clientId} onChange={handleClientChange} error={error && !clientId ? error : ''} />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              data-testid="transaction-type-select"
            >
              <option value="income">Income</option>
              <option value="expense">Expense</option>
              <option value="invoice">Invoice</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Description *
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              placeholder="What is this for?"
              data-testid="transaction-description-input"
            />
          </div>

          {/* Amount field - hidden for mileage, shown for everything else */}
          {!isMileage && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  required
                  className="w-full bg-surface-raised border border-border rounded-lg pl-8 pr-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                  placeholder="0.00"
                  data-testid="transaction-amount-input"
                />
              </div>
              {type === 'invoice' && (
                <p className="text-xs text-text-muted mt-1">
                  When marked paid, income and client total update automatically.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Date *
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              data-testid="transaction-date-input"
            />
          </div>

          {type === 'expense' && (
            <>
              {/* Expense Category Picker */}
              <div className="relative">
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Category
                </label>
                <button
                  type="button"
                  onClick={() => setShowCategoryPicker(!showCategoryPicker)}
                  className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-left text-foreground hover:bg-surface-hover transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="category-picker-button"
                >
                  {category || 'Choose a category...'}
                </button>
                {showCategoryPicker && renderCategoryPicker()}
              </div>

              {/* Tax Note Display */}
              {taxNote && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground">{taxNote}</p>
                </div>
              )}

              {/* Mileage Special Input */}
              {isMileage && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Miles Driven *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={miles}
                    onChange={(e) => handleMilesChange(e.target.value)}
                    required
                    className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                    placeholder="e.g., 25.5"
                    data-testid="transaction-miles-input"
                  />
                  {miles && amount && (
                    <p className="text-sm text-text-muted mt-1">
                      = ${amount} ({miles} miles × ${mileageRate}/mile)
                    </p>
                  )}
                  <p className="text-xs text-text-muted mt-1">
                    Rate: ${mileageRate}/mile (2026 IRS standard rate)
                  </p>
                </div>
              )}

              {/* Tax Deductible Checkbox */}
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={taxDeductible}
                    onChange={(e) => setTaxDeductible(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-border bg-surface-raised text-primary focus:ring-primary focus:ring-offset-0"
                    data-testid="transaction-tax-deductible-checkbox"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-foreground block mb-1">
                      Tax Deductible Expense
                    </span>
                    <span className="text-xs text-text-secondary">
                      Check this if this expense is tax deductible for your business
                    </span>
                  </div>
                </label>
              </div>

              {/* Meals Deductible Amount Display */}
              {deductibleAmount !== null && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-sm text-foreground">
                    <span className="font-medium">${parseFloat(amount).toFixed(2)}</span> spent · 
                    <span className="font-medium text-blue-600"> ${deductibleAmount.toFixed(2)}</span> deductible (50%)
                  </p>
                </div>
              )}

              {/* Disclaimer */}
              <div className="text-xs text-text-muted pt-2">
                These categories are general guidance, not tax advice. Consult a licensed tax professional for your specific situation.
              </div>
            </>
          )}

          {type === 'invoice' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Due Date
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                data-testid="transaction-duedate-input"
              />
            </div>
          )}

          {error && !(!clientId) && (
            <div className="text-alert text-sm bg-alert/10 border border-alert/20 rounded-lg p-3">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 bg-secondary text-foreground hover:bg-secondary/80 border border-border rounded-lg px-4 py-2 font-medium transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary text-background hover:bg-primary/90 rounded-lg px-4 py-2 font-medium transition-colors disabled:opacity-50"
              data-testid="submit-button"
            >
              {loading ? 'Creating...' : `Create ${type === 'invoice' ? 'Invoice' : type === 'income' ? 'Income' : 'Expense'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
